<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ReminderModal from './ReminderModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showReminderModal, toggleReminderModal] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  // Pin / Unpin — pinned conversations sort to the top of inbox views.
  items.push(
    currentChat.value.pinned_at
      ? {
          icon: 'i-lucide-pin-off',
          label: 'Unpin conversation',
          action: 'unpin',
          value: 'unpin',
        }
      : {
          icon: 'i-lucide-pin',
          label: 'Pin to top',
          action: 'pin',
          value: 'pin',
        }
  );

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Set a reminder on this conversation.
  items.push({
    icon: 'i-lucide-alarm-clock',
    label: 'Set a reminder',
    action: 'set_reminder',
    value: 'set_reminder',
  });

  // Mark as spam — adds sender to account deny list + resolves the
  // conversation. We rely on the confirm() dialog as the safeguard since
  // the dropdown component doesn't support visual dividers.
  items.push({
    icon: 'i-lucide-ban',
    label: 'Mark as spam',
    action: 'mark_as_spam',
    value: 'mark_as_spam',
  });

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'pin' || action === 'unpin') {
    await ConversationApi.togglePin(currentChat.value.id);
    // Optimistic local update so sidebar reorders without a refresh.
    currentChat.value.pinned_at = action === 'pin' ? Math.floor(Date.now() / 1000) : null;
    useAlert(action === 'pin' ? 'Conversation pinned' : 'Pin removed');
  } else if (action === 'set_reminder') {
    toggleReminderModal();
  } else if (action === 'mark_as_spam') {
    const ok = window.confirm(
      'Mark as spam? This adds the sender to your deny list, labels this conversation spam, and resolves it.'
    );
    if (!ok) return;
    await ConversationApi.markAsSpam(currentChat.value.id);
    useAlert('Marked as spam, sender added to deny list');
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <ReminderModal
      v-if="showReminderModal"
      :show="showReminderModal"
      :conversation-id="currentChat.id"
      @close="toggleReminderModal(false)"
    />
  </div>
</template>
